import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import * as logger from 'universal-logger';
import { withSagas } from 'decorators/Sagas';
import trackDwell from 'decorators/TrackDwell/TrackDwell';
import { decorateWith } from 'helpers/decorator';
import { deepExtract } from 'helpers/deep';
import { formatPrice } from 'helpers/promotions';
import { isAbsent, isEmpty } from 'helpers/verify';
import { selectIsClient } from 'redux/modules/device';
import { selectMsaId } from 'redux/modules/features/homepage';
import { msas, msaCreditStatById, plansByMsa, promotionByAlias } from 'redux/saga/entities/bound';
import { msaByIdentifier } from 'redux/saga/selectors/msas';
import { PlanSelection } from './PlanSelection';
import { loadSaga, watchSaga, loadCardData, selectPlan } from './plan-selection.saga';
import { messages } from './PlanSelection.messages';

function formatPlanDetails(intl, plan) {
  const {
    credits_per_cycle: creditsPerCycle,
    max_class_estimate: maxClassEstimate,
    min_class_estimate: minClassEstimate,
  } = plan;

  if ([creditsPerCycle, maxClassEstimate, minClassEstimate].some(isAbsent)) {
    logger.warn(`Incomplete plan data for plan [${plan.id}]`);
    return '';
  }

  // NOTE: non-breaking elements to ensure proper formatting
  return intl.formatMessage(messages.planDetails, {
    creditsPerCycle,
    minClassEstimate,
    maxClassEstimate,
  });
}

function buildCustomCards(intl, msa, customCards) {
  return customCards.map(customCard => {
    const cardType = customCard.cardType || {};
    const features = customCard.features || [];

    return {
      callout: formatPrice({
        intl,
        currency: msa.currency_code,
        price: customCard.price,
      }),
      calloutNote: customCard.priceTerm || '',
      cta: deepExtract('cta.copy', customCard) || intl.formatMessage(messages.ctaSelect),
      details: customCard.details || features.map(feature => `* ${feature}`).join('\n'),
      tag: deepExtract('tag.copy', customCard, ''),
      ...(cardType.type === 'fullFare' && { itemId: cardType.planId, type: 'plan' }),
      ...(cardType.type === 'msaOffer' && { itemId: cardType.msaOfferId, type: 'offer' }),
    };
  });
}

function buildPlanCards(intl, msa, plans) {
  return plans.map(plan => ({
    callout: formatPrice({
      intl,
      currency: msa.currency_code,
      price: plan.price,
    }),
    calloutNote: intl.formatMessage(messages.standardPlanNote),
    cta: intl.formatMessage(messages.ctaSelect),
    details: formatPlanDetails(intl, plan),
    itemId: plan.id,
    type: 'plan',
  }));
}

function buildPromotionCards(intl, msa, offers) {
  return offers.map(offer => {
    const originalPrice = formatPrice({
      intl,
      currency: msa.currency_code,
      price: offer.recurring_plan.price,
    });
    const isSingleMonth = offer.initial_duration_formatted === '1 month';
    // Note: backwards compatible for non-required price terms
    const calloutNote =
      deepExtract('content.card.price_terms', offer) ||
      (isSingleMonth && intl.formatMessage(messages.firstMonthOfferNote, { originalPrice })) ||
      '';

    return {
      callout: formatPrice({
        intl,
        currency: msa.currency_code,
        price: offer.price,
      }),
      calloutNote,
      cta: deepExtract('content.card.cta_copy', offer) || intl.formatMessage(messages.ctaSelect),
      details: formatPlanDetails(intl, offer.initial_plan),
      itemId: offer.id,
      tag: deepExtract('content.card.tag', offer, ''),
      type: 'offer',
    };
  });
}

function buildCards(state, intl, msa, plansModuleConfig = {}) {
  if (isEmpty(msa)) {
    return [];
  }
  if (plansModuleConfig.type === 'checkoutPlansModuleType') {
    const plans = plansByMsa.data(state, msa.id);
    return buildPlanCards(intl, msa, plans || []);
  }
  if (plansModuleConfig.type === 'promotionPlansModuleType') {
    const key = promotionByAlias.defaultKey(null, plansModuleConfig.promotionAlias, msa.id);
    const promotion = promotionByAlias.data(state, key) || {};
    return buildPromotionCards(intl, msa, promotion.offers || []);
  }
  if (plansModuleConfig.type === 'pricingCardsPlansModuleType') {
    return buildCustomCards(intl, msa, plansModuleConfig.purchaseCards || []);
  }

  return [];
}

export const mapStateToProps = (state, props) => {
  const msaId = selectMsaId(state);
  const msa = msaByIdentifier(msas.data(state), msaId) || {};
  const msaCreditStat = msaCreditStatById.data(state, msaId) || {};
  return {
    cards: props.cards || buildCards(state, props.intl, msa, props.plansModuleType),
    creditsPerClassAverage: msaCreditStat.average_credits_per_class,
    isClient: selectIsClient(state),
    plansModuleConfig: props.plansModuleType,
    subtitleStyle: props.subtitleStyle || 'subtle',
  };
};

export const mapDispatchToProps = {
  loadCardData,
  selectPlan,
};

export const PlanSelectionContainer = decorateWith(
  trackDwell,
  withSagas({ load: loadSaga, watch: watchSaga }),
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(PlanSelection);
